import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, AlertCircle, Lock, RefreshCw, Send, CloudOff } from "lucide-react";

const DEFAULT_AREAS = ["Falkirk", "Edinburgh", "Midlothian", "West Lothian", "Other"];
const RTW_OPTIONS = [
  "I confirm I have the right to work in the UK.",
  "I require sponsorship / further checks.",
];
const EMPLOYMENT_OPTIONS = [
  { value: "Employed", label: "Employed" },
  { value: "Unemployed", label: "Unemployed" },
  { value: "In Education / Training", label: "In Education / Training" },
  { value: "Self-Employed", label: "Self-Employed" },
  { value: "Other", label: "Other" },
];
const SECTOR_OPTIONS = [
  "Construction", "Engineering", "Agriculture", "Warehousing / Logistics", "Driving / Transport",
  "Manufacturing", "Customer Service", "Hospitality", "Office / Administration", "Other", "None of the above",
];

const FIELDS = {
  // About you
  mobile: { label: "Mobile", type: "text", required: true, placeholder: "07xxx xxxxxx" },
  home_address: { label: "Home address", type: "text", placeholder: "Optional" },
  postcode: { label: "Postcode", type: "text", required: true, placeholder: "e.g. FK1 4AB" },
  local_authority: { label: "Local authority area", type: "areas", required: true },
  date_of_birth: { label: "Date of birth (required for funding / eligibility checks)", type: "date", required: true },
  right_to_work: { label: "Right to work confirmation", type: "select", options: RTW_OPTIONS, required: true },
  referral_source: { label: "Referral source", type: "text", placeholder: "e.g. Falkirk Council, Jobcentre" },
  heard_about: { label: "How did you hear about the Academy?", type: "text" },
  council_referral: { label: "Council / employability referral", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  funding_reference: { label: "Funding referral reference (if applicable)", type: "text" },
  // Work background
  work_history: {
    label: "What type of work have you done previously?", type: "textarea", required: true,
    helper: "No construction or plant experience is required. Tell us about any work, education, volunteering or practical experience you have had.",
  },
  employment_status: { label: "Are you currently:", type: "choice", options: EMPLOYMENT_OPTIONS, required: true },
  previous_sectors: { label: "Have you previously worked in: (select all that apply)", type: "multiselect", options: SECTOR_OPTIONS, required: true },
  current_employer: { label: "Current employer (if applicable)", type: "text" },
  // The opportunity
  motivation: { label: "What interests you about becoming a plant operator?", type: "textarea", required: true },
  practical_work: {
    label: "Do you enjoy practical, hands-on work?", type: "choice", required: true,
    options: [{ value: "very_much", label: "Very Much" }, { value: "quite_a_lot", label: "Quite A Lot" }, { value: "sometimes", label: "Sometimes" }, { value: "not_particularly", label: "Not Particularly" }],
  },
  outdoors_comfort: {
    label: "How do you feel about working outdoors in different weather conditions?", type: "choice", required: true,
    options: [{ value: "comfortable", label: "Comfortable" }, { value: "probably_comfortable", label: "Probably Comfortable" }, { value: "unsure", label: "Unsure" }, { value: "not_for_me", label: "Not For Me" }],
  },
  early_starts: {
    label: "Would early starts and travelling to different work sites generally be manageable for you?", type: "choice", required: true,
    options: [{ value: "yes", label: "Yes" }, { value: "usually", label: "Usually" }, { value: "unsure", label: "Unsure" }, { value: "no", label: "No" }],
  },
  long_term_career: {
    label: "Are you looking for a long-term career opportunity?", type: "choice", required: true,
    options: [{ value: "yes", label: "Yes" }, { value: "probably", label: "Probably" }, { value: "unsure", label: "Unsure" }, { value: "no", label: "No" }],
  },
  // Practical information
  driving_licence: {
    label: "Do you hold a full UK driving licence?", type: "choice", required: true,
    options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "learning", label: "Currently Learning" }],
    helper: "Not holding a licence does not rule you out — we just need to understand how you would travel.",
  },
  access_to_transport: {
    label: "Do you have access to reliable transport?", type: "choice", required: true,
    options: [{ value: "yes", label: "Yes" }, { value: "sometimes", label: "Sometimes" }, { value: "no", label: "No" }],
  },
  travel_to_sites: {
    label: "Would you be able to travel to training and different work sites?", type: "choice", required: true,
    options: [{ value: "yes", label: "Yes" }, { value: "usually", label: "Usually" }, { value: "unsure", label: "Unsure" }, { value: "no", label: "No" }],
  },
  earliest_availability: { label: "Earliest availability", type: "text", required: true, placeholder: "e.g. Immediately / 2 weeks" },
};

const WS_QUESTIONS = [
  { key: "ws_1", q: "When I am learning something completely new, I normally prefer to:", options: ["Watch someone demonstrate it first", "Have it explained clearly before trying", "Have a go and learn through practice", "Ask questions as I go"] },
  { key: "ws_2", q: "If someone tells me I have made a mistake, I am most likely to:", options: ["Want to understand exactly what I did wrong", "Correct it and try again", "Feel frustrated initially but work through it", "Ask them to show me a better way"] },
  { key: "ws_3", q: "If I am unsure about an instruction at work, I would usually:", options: ["Ask before continuing", "Watch what others are doing", "Try to work it out myself first", "Carry on if I think I understand most of it"] },
  { key: "ws_4", q: "When working on a task, I prefer:", options: ["Clear instructions and a plan", "A goal and freedom to decide how to achieve it", "Working things out with other people", "Learning by trial and error"] },
  { key: "ws_5", q: "Which sounds most like you?", options: ["I normally take my time and check things", "I like getting things done quickly", "I like keeping everyone involved", "I like finding different ways of solving problems"] },
  { key: "ws_6", q: "If a job is repetitive, I normally:", options: ["Stay focused because doing it properly matters", "Get bored quite quickly", "Look for ways to improve the process", "Prefer to change tasks regularly"] },
  { key: "ws_7", q: "If somebody more experienced gives me detailed instructions:", options: ["I appreciate knowing exactly what is expected", "I listen, but prefer to find my own way afterwards", "I ask questions until I understand it", "I sometimes feel I am being over-managed"] },
  { key: "ws_8", q: "When things start going wrong, I tend to:", options: ["Slow down and work out what has changed", "React quickly and try to solve it", "Ask someone for help", "Keep going unless the problem becomes serious"] },
  { key: "ws_9", q: "In a team, I am usually the person who:", options: ["Gets on quietly with the job", "Takes the lead", "Helps everyone work together", "Spots problems or improvements"] },
  { key: "ws_10", q: "What matters most to you at work?", options: ["Doing the job correctly", "Getting results", "Being part of a good team", "Learning and progressing"] },
  { key: "ws_11", q: "If I do not understand something first time:", options: ["I ask for it to be explained again", "I try it until I work it out", "I watch someone else do it", "I can become frustrated"] },
  { key: "ws_12", q: "When a rule seems inconvenient:", options: ["I still follow it until I understand why it can be changed", "I use my judgement", "I ask my supervisor whether there is another way", "I tend to ignore rules that do not seem necessary"] },
];
const LETTERS = ["a", "b", "c", "d"];

const SCENARIOS = [
  { key: "sc_1", q: "A supervisor gives you an instruction but you are not completely sure what they mean. What would you do?" },
  { key: "sc_2", q: "You notice a colleague taking a shortcut that saves time but does not look particularly safe. What would you do?" },
  { key: "sc_3", q: "You have been shown how to complete a task twice but are still struggling with it. What would you do next?" },
  { key: "sc_4", q: "You realise you have made a mistake that may delay the job. What would you do?" },
  { key: "sc_5", q: "You are asked to do something you have never done before. How would you approach it?" },
];

const SECTIONS = [
  { id: "about", title: "About You", desc: "Your details and how to reach you — postcode and local authority help us check funding.", fields: ["mobile", "home_address", "postcode", "local_authority", "date_of_birth", "right_to_work", "referral_source", "heard_about", "council_referral", "funding_reference"] },
  { id: "background", title: "Your Work Background", desc: "Anything counts — work, education, volunteering or practical experience. No construction or plant experience is required.", fields: ["work_history", "employment_status", "previous_sectors", "current_employer"] },
  { id: "opportunity", title: "The Opportunity", desc: "Why this interests you and how it fits your life.", fields: ["motivation", "practical_work", "outdoors_comfort", "early_starts", "long_term_career"] },
  { id: "workstyle", title: "How You Prefer to Work", desc: "There are no right or wrong answers. These questions help us understand how you prefer to work and learn. Previous plant or construction experience is not required.", workstyle: true },
  { id: "scenarios", title: "Workplace Scenarios", desc: "A few short everyday situations. Tell us honestly what you would do — there are no trick questions.", scenarios: true },
  { id: "practical", title: "Practical Information", desc: "Licence and travel — this helps us plan, it does not rule you out.", fields: ["driving_licence", "access_to_transport", "travel_to_sites", "earliest_availability"] },
  { id: "review", title: "Review & Submit", desc: "Check your details, then submit your application.", fields: [] },
];

const REQUIRED_KEYS = [
  ...SECTIONS.flatMap((s) => (s.fields || []).filter((k) => FIELDS[k].required)),
  ...WS_QUESTIONS.map((q) => q.key),
  ...SCENARIOS.map((s) => s.key),
];

const inputCls = "bg-obsidian border-steel text-charcoal h-11";

function isFilled(v) {
  if (Array.isArray(v)) return v.length > 0;
  return String(v || "").trim().length > 0;
}

function displayValue(k, form) {
  const v = form[k];
  if (Array.isArray(v)) return v.length ? v.join(", ") : "";
  if (FIELDS[k]?.type === "choice" || FIELDS[k]?.type === "select") {
    const opt = (FIELDS[k].options || []).find((o) => (typeof o === "string" ? o : o.value) === v);
    if (opt) return typeof opt === "string" ? opt : opt.label;
  }
  return v || "";
}

function SaveStatus({ state, error, onRetry }) {
  if (state === "saving") {
    return <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500" data-testid="save-status-saving">
      <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Saving…
    </span>;
  }
  if (state === "saved") {
    return <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-emerald-300" data-testid="save-status-saved">
      <CheckCircle2 className="h-3.5 w-3.5" /> Saved
    </span>;
  }
  if (state === "error") {
    return (
      <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-red-400" data-testid="save-status-error">
        <CloudOff className="h-3.5 w-3.5" /> Unable to save
        <button type="button" onClick={onRetry} className="underline text-lomond" data-testid="save-retry-btn">Retry</button>
      </span>
    );
  }
  return <span className="text-xs font-mono uppercase tracking-widest text-slate-600" data-testid="save-status-idle">Draft</span>;
}

function FieldControl({ fieldKey, form, set, toggleMulti, areas, editable }) {
  const f = FIELDS[fieldKey];
  const val = form[fieldKey] || (f.type === "multiselect" ? [] : "");
  const testId = `field-${fieldKey.replace(/_/g, "-")}`;
  const wide = f.type === "textarea" || fieldKey === "home_address";
  return (
    <div className={`space-y-1.5 ${wide || f.type === "choice" || f.type === "multiselect" ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={fieldKey} className="text-slate-600 text-sm">
        {f.label} {f.required && <span className="text-lomond">*</span>}
      </Label>
      {f.helper && <p className="text-xs text-slate-500">{f.helper}</p>}
      {f.type === "textarea" ? (
        <Textarea id={fieldKey} value={val} onChange={(e) => set(fieldKey, e.target.value)} placeholder={f.placeholder}
          disabled={!editable} className="bg-obsidian border-steel text-charcoal min-h-24" data-testid={testId} />
      ) : f.type === "select" || f.type === "areas" ? (
        <Select value={val} onValueChange={(v) => set(fieldKey, v)} disabled={!editable}>
          <SelectTrigger id={fieldKey} className={inputCls} data-testid={testId}>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {(f.type === "areas" ? areas : f.options).map((o) => {
              const optionVal = typeof o === "string" ? o : o.value;
              const optionLabel = typeof o === "string" ? o : o.label;
              return <SelectItem key={optionVal} value={optionVal} data-testid={`option-${fieldKey.replace(/_/g, "-")}-${optionVal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{optionLabel}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      ) : f.type === "choice" ? (
        <div className="flex flex-wrap gap-2" data-testid={testId}>
          {f.options.map((o) => (
            <button key={o.value} type="button" disabled={!editable} onClick={() => set(fieldKey, o.value)}
              className={`px-4 py-2.5 rounded-sm border text-sm transition-colors ${
                val === o.value ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-600 hover:border-lomond hover:text-lomond"
              }`}
              data-testid={`${testId}-${o.value}`}>
              {o.label}
            </button>
          ))}
        </div>
      ) : f.type === "multiselect" ? (
        <div className="flex flex-wrap gap-2" data-testid={testId}>
          {f.options.map((o) => {
            const active = val.includes(o);
            return (
              <button key={o} type="button" disabled={!editable} onClick={() => toggleMulti(fieldKey, o)}
                className={`px-3 py-2 rounded-sm border text-xs transition-colors ${
                  active ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-600 hover:border-lomond hover:text-lomond"
                }`}
                data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {o}
              </button>
            );
          })}
        </div>
      ) : (
        <Input id={fieldKey} type={f.type} value={val} onChange={(e) => set(fieldKey, e.target.value)} placeholder={f.placeholder}
          disabled={!editable} className={inputCls} data-testid={testId} />
      )}
    </div>
  );
}

export default function ApplicantProfile() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const draftKey = `loa_profile_draft_${user?.id}`;

  const [form, setForm] = useState({});
  const [areas, setAreas] = useState(DEFAULT_AREAS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [completion, setCompletion] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [editRequested, setEditRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);
  const formRef = useRef({});
  formRef.current = form;

  const editable = !submitted || editUnlocked;

  const save = useCallback(async (payload) => {
    setSaveState("saving");
    setSaveError("");
    try {
      const { data } = await api.put("/applicant/profile", { profile: payload });
      setSaveState("saved");
      setCompletion(data.completion_pct ?? 0);
      localStorage.setItem(draftKey, JSON.stringify({ form: payload, saved_at: data.updated_at, confirmed: true }));
      return true;
    } catch (e) {
      setSaveState("error");
      setSaveError(apiError(e));
      return false;
    }
  }, [draftKey]);

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      localStorage.setItem(draftKey, JSON.stringify({ form: next, saved_at: new Date().toISOString(), confirmed: false }));
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => save(next), 1500);
      return next;
    });
  };

  const toggleMulti = (k, option) => {
    const current = Array.isArray(formRef.current[k]) ? formRef.current[k] : [];
    let next;
    if (option === "None of the above") {
      next = current.includes(option) ? [] : [option];
    } else {
      next = current.includes(option)
        ? current.filter((x) => x !== option)
        : [...current.filter((x) => x !== "None of the above"), option];
    }
    set(k, next);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let server = null;
      try {
        const { data } = await api.get("/applicant/profile");
        server = data || {};
      } catch {
        setLoadError(true);
      }
      let initial = server || {};
      let recovered = false;
      try {
        const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
        if (draft?.form && !draft.confirmed && String(draft.saved_at) > String(server?.updated_at || "")) {
          initial = { ...initial, ...draft.form };
          recovered = true;
        }
      } catch { /* ignore bad draft */ }
      if (cancelled) return;
      setForm(initial);
      setSubmitted(server?.application_status === "submitted");
      setSubmittedAt(server?.submitted_at || null);
      setEditUnlocked(!!server?.edit_unlocked);
      setEditRequested(!!server?.edit_requested);
      setCompletion(server?.completion_pct ?? 0);
      setLoading(false);
      const locked = server?.application_status === "submitted" && !server?.edit_unlocked;
      if (recovered && server && !locked) save(initial);
    })();
    api.get("/config").then((r) => {
      if (r.data?.funding_areas?.length) setAreas(r.data.funding_areas);
    }).catch(() => {});
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAndContinue = async () => {
    const ok = await save(formRef.current);
    if (ok) setStep((s) => Math.min(s + 1, SECTIONS.length - 1));
  };

  const submitApplication = async () => {
    setSubmitting(true);
    try {
      const ok = await save(formRef.current);
      if (!ok) { setSubmitting(false); return; }
      await api.post("/applicant/profile/submit");
      setSubmitted(true);
      setEditUnlocked(false);
      setSubmittedAt(new Date().toISOString());
      setCompletion(100);
      toast.success("Application submitted. Lomond will review it and unlock your online assessment.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const requestEdit = async () => {
    try {
      await api.post("/applicant/profile/request-edit");
      setEditRequested(true);
      toast.success("Change request sent to Lomond.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="profile-loading" />;

  const missing = REQUIRED_KEYS.filter((k) => !isFilled(form[k]));
  const missingLabel = (k) => {
    if (FIELDS[k]) return FIELDS[k].label;
    const ws = WS_QUESTIONS.find((q) => q.key === k);
    if (ws) return `Work-style question ${ws.key.split("_")[1]}`;
    const sc = SCENARIOS.find((s) => s.key === k);
    if (sc) return `Scenario ${sc.key.split("_")[1]}`;
    return k;
  };
  const gotoField = (k) => {
    const idx = SECTIONS.findIndex((s) =>
      (s.fields || []).includes(k) ||
      (s.workstyle && k.startsWith("ws_")) ||
      (s.scenarios && k.startsWith("sc_")));
    if (idx >= 0) setStep(idx);
  };

  const sectionSummary = (section) => {
    if (section.workstyle) {
      return WS_QUESTIONS.map((q, i) => {
        const letter = form[q.key];
        const idx = LETTERS.indexOf(letter);
        return { key: q.key, label: `${i + 1}. ${q.q}`, value: idx >= 0 ? `${letter.toUpperCase()}. ${q.options[idx]}` : "" };
      });
    }
    if (section.scenarios) {
      return SCENARIOS.map((s, i) => ({ key: s.key, label: `${i + 1}. ${s.q}`, value: form[s.key] || "" }));
    }
    return section.fields.map((k) => ({ key: k, label: FIELDS[k].label, value: displayValue(k, form) }));
  };

  // ---------- Submitted & locked view ----------
  if (submitted && !editUnlocked) {
    return (
      <div className="max-w-3xl space-y-6" data-testid="applicant-profile-page">
        <div>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl">My Application</h1>
        </div>
        <div className="bg-slate-panel border border-emerald-500/40 rounded-sm p-6" data-testid="submitted-banner">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading font-bold uppercase text-emerald-300">Application submitted</h2>
              <p className="text-sm text-slate-500 mt-1">
                {submittedAt ? `Received ${new Date(submittedAt).toLocaleString("en-GB")}. ` : ""}
                Your details are now locked while Lomond reviews your application.
              </p>
              {editRequested ? (
                <p className="text-sm text-amber-300 mt-3" data-testid="edit-requested-note">
                  Change request sent — Lomond will unlock your application if an edit is approved.
                </p>
              ) : (
                <Button variant="outline" size="sm" onClick={requestEdit}
                  className="mt-4 border-steel text-slate-700 bg-transparent hover:border-lomond hover:text-lomond" data-testid="request-edit-btn">
                  Request a change
                </Button>
              )}
            </div>
          </div>
        </div>
        {SECTIONS.filter((s) => s.id !== "review").map((section) => (
          <section key={section.id} className="bg-slate-panel border border-steel rounded-sm p-6" data-testid={`submitted-section-${section.id}`}>
            <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">{section.title}</h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {sectionSummary(section).map((row) => (
                <div key={row.key} className={section.workstyle || section.scenarios || row.key === "work_history" || row.key === "motivation" ? "sm:col-span-2" : ""}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{row.label}</p>
                  <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap" data-testid={`submitted-field-${row.key.replace(/_/g, "-")}`}>{row.value || "—"}</p>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  // ---------- Editable wizard ----------
  const section = SECTIONS[step];
  return (
    <div className="max-w-3xl space-y-6" data-testid="applicant-profile-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl">Register Your Interest</h1>
          <p className="text-sm text-slate-500 mt-1">
            Your progress saves automatically as you type. Come back any time — nothing is lost.
          </p>
        </div>
        <SaveStatus state={saveState} error={saveError} onRetry={() => save(formRef.current)} />
      </div>

      <div className="bg-lomond/10 border border-lomond/40 rounded-sm px-4 py-3" data-testid="no-experience-banner">
        <p className="text-sm font-heading font-bold uppercase tracking-wide text-lomond">No previous plant experience required</p>
        <p className="text-sm text-slate-600 mt-1">
          We are looking for people with the right attitude, willingness to learn, reliability, practical aptitude and a strong safety mindset.
          You are not expected to know anything about construction or machinery yet.
        </p>
      </div>

      {welcome && (
        <p className="bg-lomond/10 border border-lomond/40 text-lomond text-sm rounded-sm px-4 py-3" data-testid="welcome-notice">
          Welcome to the Academy. Work through each section — everything is saved as you go.
        </p>
      )}
      {loadError && (
        <p className="bg-amber-400/10 border border-amber-400/40 text-amber-300 text-sm rounded-sm px-4 py-3" data-testid="load-error-notice">
          Couldn't reach the server — showing your last local draft. Changes will save when the connection returns.
        </p>
      )}
      {editUnlocked && submitted && (
        <p className="bg-amber-400/10 border border-amber-400/40 text-amber-300 text-sm rounded-sm px-4 py-3" data-testid="edit-unlocked-notice">
          Lomond has unlocked your application for editing. Make your changes and re-submit from the Review step.
        </p>
      )}
      {saveState === "error" && (
        <p className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="save-error-banner">
          {saveError || "Unable to save."} Your entries are still on screen and stored in this browser as a backup — nothing has been lost.
        </p>
      )}

      <div>
        <div className="flex justify-between text-xs font-mono uppercase tracking-widest text-slate-500 mb-1.5">
          <span>Application completion</span>
          <span className="text-lomond" data-testid="completion-pct">{completion}%</span>
        </div>
        <div className="h-1.5 bg-elevated rounded-sm overflow-hidden">
          <div className="h-full bg-lomond transition-all" style={{ width: `${completion}%` }} data-testid="completion-bar" />
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="wizard-nav">
        {SECTIONS.map((s, i) => (
          <button key={s.id} type="button" onClick={() => setStep(i)}
            className={`px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border whitespace-nowrap transition-colors ${
              i === step ? "bg-lomond text-black border-lomond font-bold" : "border-steel text-slate-500 hover:border-lomond hover:text-lomond"
            }`}
            data-testid={`wizard-nav-${s.id}`}>
            {i + 1}. {s.title}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => e.preventDefault()} data-testid="profile-form">
        <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid={`wizard-section-${section.id}`}>
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">{section.title}</h2>
          <p className="text-xs text-slate-500 mt-1 mb-5">{section.desc}</p>

          {section.workstyle ? (
            <div className="space-y-6">
              {WS_QUESTIONS.map((q, qi) => (
                <div key={q.key} className="space-y-2" data-testid={`ws-question-${qi + 1}`}>
                  <p className="text-sm text-slate-700 font-semibold">
                    {qi + 1}. {q.q} <span className="text-lomond">*</span>
                  </p>
                  <div className="grid gap-2">
                    {q.options.map((opt, oi) => {
                      const active = form[q.key] === LETTERS[oi];
                      return (
                        <button key={oi} type="button" disabled={!editable} onClick={() => set(q.key, LETTERS[oi])}
                          className={`text-left px-4 py-3 rounded-sm border text-sm transition-colors flex items-start gap-3 ${
                            active ? "bg-lomond text-black border-lomond font-semibold" : "border-steel text-slate-600 hover:border-lomond hover:text-lomond"
                          }`}
                          data-testid={`${q.key}-option-${LETTERS[oi]}`}>
                          <span className={`font-mono text-xs font-bold mt-0.5 ${active ? "text-black" : "text-lomond"}`}>{LETTERS[oi].toUpperCase()}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : section.scenarios ? (
            <div className="space-y-5">
              {SCENARIOS.map((s, si) => (
                <div key={s.key} className="space-y-1.5" data-testid={`scenario-block-${si + 1}`}>
                  <Label htmlFor={s.key} className="text-slate-600 text-sm">
                    {si + 1}. {s.q} <span className="text-lomond">*</span>
                  </Label>
                  <Textarea id={s.key} value={form[s.key] || ""} onChange={(e) => set(s.key, e.target.value)}
                    placeholder="A sentence or two is plenty." disabled={!editable}
                    className="bg-obsidian border-steel text-charcoal min-h-20" data-testid={`field-sc-${si + 1}`} />
                </div>
              ))}
            </div>
          ) : section.id !== "review" ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {section.fields.map((k) => (
                <FieldControl key={k} fieldKey={k} form={form} set={set} toggleMulti={toggleMulti} areas={areas} editable={editable} />
              ))}
            </div>
          ) : (
            <div className="space-y-5" data-testid="review-panel">
              {missing.length > 0 ? (
                <div className="bg-amber-400/10 border border-amber-400/40 rounded-sm p-4">
                  <p className="text-sm text-amber-300 font-semibold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Required before you can submit:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {missing.map((k) => (
                      <li key={k} className="text-sm text-slate-600">
                        — {missingLabel(k)}{" "}
                        <button type="button" className="text-lomond underline text-xs"
                          onClick={() => gotoField(k)}
                          data-testid={`missing-goto-${k.replace(/_/g, "-")}`}>
                          go to field
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-sm p-4" data-testid="review-ready">
                  <p className="text-sm text-emerald-300 font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> All required details are complete.
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Submitting is final — your application locks and Lomond begins its review. Your online aptitude assessment unlocks next.
                  </p>
                </div>
              )}
              {SECTIONS.filter((s) => s.id !== "review").map((s) => (
                <div key={s.id} className="border-t border-steel pt-4">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-lomond mb-2">{s.title}</p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {sectionSummary(s).map((row) => (
                      <div key={row.key} className={s.workstyle || s.scenarios || row.key === "work_history" || row.key === "motivation" ? "sm:col-span-2" : ""}>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{row.label}</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid={`review-field-${row.key.replace(/_/g, "-")}`}>{row.value || "—"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Button onClick={submitApplication} disabled={submitting || missing.length > 0 || saveState === "saving"}
                className="w-full sm:w-auto bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400] h-12 px-10"
                data-testid="submit-application-btn">
                <Send className="h-4 w-4 mr-2" /> {submitting ? "Submitting..." : submitted ? "Re-submit Application" : "Submit Application"}
              </Button>
            </div>
          )}
        </section>

        <div className="flex items-center justify-between gap-3 mt-6">
          <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}
            className="border-steel text-slate-700 bg-transparent hover:border-lomond hover:text-lomond" data-testid="prev-section-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {section.id !== "review" && (
            <Button type="button" onClick={saveAndContinue} disabled={saveState === "saving"}
              className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="profile-save-btn">
              Save &amp; Continue <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
